import { Input } from '@agavi/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';

import LeftPanel from '@/components/auth/left-panel';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { api } from '@/lib/api';
import { loginSchema, type LoginFormValues } from '@/types/auth';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(data: LoginFormValues) {
    setError('');
    setLoading(true);
    try {
      /*
       * Two shapes, and which one comes back is the server's call, not ours: a trusted device gets
       * the access token straight from `/auth/login` and never sees the code screen. The client
       * cannot ask for that — the device cookie is HttpOnly, so only the server knows whether this
       * machine is trusted.
       */
      const res = await api.post<{ accessToken?: string }>('/auth/login', data);
      if (res.accessToken) {
        localStorage.setItem('token', res.accessToken);
        navigate('/app');
        return;
      }
      navigate('/confirm', { state: { email: data.email } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className='flex min-h-screen bg-ws-bg'>
      <LeftPanel />

      <div className='flex flex-1 items-center justify-center px-6'>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className='w-full max-w-[320px] space-y-8'
          >
            {/* Header */}
            <div className='text-center space-y-2'>
              <h1 className='text-3xl font-semibold text-ws-text'>
                {t('auth.login.title')}
              </h1>
              <p className='text-base text-ws-muted'>
                {t('auth.login.subtitle')}
              </p>
            </div>

            {/* Fields */}
            <div className='space-y-6'>
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-base font-medium'>
                      {t('auth.login.email')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type='email'
                        placeholder={t('auth.login.emailPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <p className='text-xs text-ws-muted'>
                      {t('auth.login.emailHint')}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-base font-medium'>
                      {t('auth.login.password')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type='password'
                        placeholder={t('auth.login.passwordPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <p className='text-xs text-ws-muted'>
                      {t('auth.login.passwordHint')}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Error */}
            {error && <p role='alert' className='text-center text-sm text-ws-error-fg'>{error}</p>}

            {/* Actions */}
            <div className='space-y-4'>
              <Button type='submit' variant='primary' size='lg' block busy={loading}>
                {loading ? t('auth.login.submitting') : t('auth.login.submit')}
              </Button>

              <p className='text-center text-base text-ws-text'>
                {t('auth.login.forgotPassword')}{' '}
                <Link
                  to='/forgot-password'
                  className='text-ws-text-link underline-offset-4 hover:underline'
                >
                  {t('auth.login.resetHere')}
                </Link>
              </p>

              <Button variant='secondary' size='lg' block onClick={() => navigate('/signup')}>
                {t('auth.login.noAccount')}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
